// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {CollapsibleTheme, SectionEl} from 'common/components/section';
import {TooltipEl} from 'common/components/tooltip';
import {ConfirmEl} from 'common/components/confirm';
import {SvgIconEl} from 'common/components/svg-icon';
import {SpinnerEl} from 'common/components/spinner';
import {TEXT_HIGHLIGHT_THEME, TextHighlightEl} from 'common/components/text-highlight';
import {TrackedUserIconEl} from 'common/widgets/tracked-user-icon';
import {IconSizeType} from 'common/constants';
import {Placement} from 'common/components/tooltip/types';
import {THEME} from 'common/components/confirm/types';

import type {ProfileUserSummary} from './index';

const Aliases = function ({
  distinctId,
  aliases,
  aliasesLoading,
  aliasesError,
}: {
  distinctId: string;
  aliases: string[];
  aliasesLoading: boolean;
  aliasesError?: string;
}) {
  if (aliasesLoading) {
    return (
      <>
        <div sel='.user-info-default-alias' attrs={{slot: `title`, elref: `profileUserId`}}>
          {distinctId}
        </div>
        <SpinnerEl />
      </>
    );
  }

  return (
    <SectionEl
      attrs={{
        'is-closed': true,
        theme: CollapsibleTheme.INLINE,
        elref: `profileAliases`,
      }}
    >
      <div sel='.user-info-default-alias' attrs={{slot: `title`, elref: `profileUserId`}}>
        {distinctId}
      </div>
      <div sel='.user-info-aliases-container'>
        {aliases.map((alias) => (
          <div sel='.user-info-alias'>{alias}</div>
        ))}
      </div>
      {aliasesError && <div sel='.user-info-alias-error'>{aliasesError}</div>}
    </SectionEl>
  );
};

export function template(this: ProfileUserSummary) {
  const {showConfirmDeleteProfile} = this.state;
  const {
    'aliases-loading': aliasesLoading,
    aliases,
    'aliases-error': aliasesError,
    'distinct-id': distinctId,
    'profile-summary': profileSummary,
    'user-properties-loading': propertiesLoading,
  } = this.attrs();
  const {t} = this.getContext(`i18n`);

  if (!aliases) {
    // attrs have not arrived yet
    return <div />;
  }
  const {buttonAttrs: deleteButtonAttrs, tooltip: deleteButtonTooltip} = this.helpers.getDeleteProfileButtonAttrs();

  const detailTypes = [
    {
      icon: `profile`,
      title: t(`profile.distinct-id`),
      body: (
        <Aliases
          distinctId={distinctId}
          aliases={aliases}
          aliasesLoading={aliasesLoading}
          aliasesError={aliasesError ? t(`profile.alias-error`) : undefined}
        />
      ),
    },
    {
      icon: `location`,
      title: t(`profile.location`),
      body: profileSummary.lastLocation,
      elref: `profileLastLocation`,
    },
    {
      icon: `calendar`,
      title: t(`profile.updated-at`),
      body: profileSummary.lastSeen,
      elref: `profileLastSeen`,
    },
  ];

  return (
    <div sel='.profile-user-summary'>
      <div sel='.profile-user-summary-wrapper'>
        <div sel='.mp-panel-section'>
          <div sel='.user-profile-header'>
            <div sel='.profile-header' class={{'sidebar-mode': this.attr(`sidebar-mode`)}}>
              <div sel='.avatar'>
                <TrackedUserIconEl
                  attrs={{
                    'distinct-id': distinctId,
                    'img-src': profileSummary.avatarUrl,
                    email: profileSummary.email,
                    size: IconSizeType.XL,
                  }}
                />
              </div>
              <div
                sel='.name'
                attrs={{
                  elref: `profileName`,
                  title: profileSummary.name,
                }}
              >
                {propertiesLoading ? <div sel='.mp-skeleton-medium' /> : profileSummary.name}
              </div>
              <div
                sel='.email'
                attrs={{
                  elref: `profileEmail`,
                }}
              >
                <div>{profileSummary.email}</div>
                {this.helpers.hasUnsubscribed() && (
                  <TextHighlightEl
                    attrs={{
                      length: `wide`,
                      theme: TEXT_HIGHLIGHT_THEME.FAILED,
                      text: t(`profile.email-unsubscribed`),
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        {!this.attr(`sidebar-mode`) && (
          <div
            attrs={{elref: propertiesLoading ? `profileDetailsLoading` : `profileDetails`}}
            sel={propertiesLoading ? `.mp-skeleton-wrapper` : ``}
          >
            {detailTypes.map(({icon, title, body, elref}) =>
              propertiesLoading ? (
                <div sel='.mp-panel-section'>
                  <div sel='.mp-skeleton-large' />
                </div>
              ) : (
                <div sel='.mp-panel-section'>
                  <div sel='.user-info-property' class={{'sidebar-mode': this.attr(`sidebar-mode`)}}>
                    <SvgIconEl attrs={{icon}} />
                    <div sel='.user-info-detail'>
                      <div sel='.user-info-detail-type'>{title}</div>
                      <div
                        sel='.user-info-detail-data'
                        attrs={{
                          elref,
                        }}
                      >
                        {body}
                      </div>
                    </div>
                  </div>
                </div>
              ),
            )}
            {!this.attr(`hide-delete`) ? (
              <div sel='.mp-panel-section.delete-profile-button'>
                <span>
                  <ButtonEl
                    attrs={deleteButtonAttrs}
                    on={{
                      click: !deleteButtonAttrs?.disabled && this.helpers.openDeleteProfileConfirmation,
                    }}
                  >
                    {t(`profile.delete`)}
                  </ButtonEl>
                  <TooltipEl attrs={{placement: Placement.Top, offset: 10}}>{deleteButtonTooltip}</TooltipEl>
                </span>
                <ConfirmEl
                  attrs={{
                    theme: THEME.DELETE,
                    'confirm-button-label': t(`profile.delete`),
                    open: showConfirmDeleteProfile,
                  }}
                  on={{
                    submit: this.helpers.handleModalButton,
                  }}
                >
                  <div>
                    <div sel='.mp-confirm-title'>{t(`profile.delete`)}?</div>
                    <div sel='.mp-confirm-description'>{t(`profile.delete-confirm-body`)}</div>
                  </div>
                </ConfirmEl>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
